import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { KeyRound, Users, CheckCircle, AlertCircle } from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function Setup() {
  const [setupKey, setSetupKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState('');

  const handleReset = async () => {
    if (!setupKey) {
      toast.error('Please enter the setup key');
      return;
    }

    setLoading(true);
    setError('');
    setCredentials(null);

    try {
      const response = await fetch(`${API}/api/brands/setup/reset-all-admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setup_key: setupKey })
      });

      const data = await response.json();

      if (data.success) {
        setCredentials(data.credentials);
        toast.success('All credentials reset successfully!');
      } else {
        setError(data.detail || 'Failed to reset credentials');
        toast.error(data.detail || 'Invalid setup key');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
            <KeyRound className="w-8 h-8 text-amber-500" />
          </div>
          <CardTitle className="text-2xl text-white">Fundle Brain Setup</CardTitle>
          <p className="text-slate-400 mt-2">Reset all brand admin credentials</p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {!credentials ? (
            <>
              <div className="space-y-2">
                <label className="text-sm text-slate-300">Setup Key</label>
                <Input
                  type="password"
                  placeholder="Enter setup key..."
                  value={setupKey}
                  onChange={(e) => setSetupKey(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white"
                  data-testid="setup-key-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button 
                onClick={handleReset} 
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                data-testid="reset-credentials-btn"
              >
                {loading ? 'Resetting...' : 'Reset All Credentials'}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Contact your administrator if you don't have the setup key.
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-green-400 mb-4">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Credentials Reset Successfully!</span>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {credentials.map((cred, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-900/50 rounded-lg p-3 border border-slate-700"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-amber-500" />
                      <span className="font-medium text-white">{cred.brand}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-400">Email:</span>
                        <p className="text-slate-200 font-mono text-xs">{cred.email}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Password:</span>
                        <p className="text-slate-200 font-mono text-xs">{cred.password}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 border-slate-600 text-slate-300"
                  onClick={() => setCredentials(null)}
                >
                  Reset Again
                </Button>
                <Button 
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={() => window.location.href = '/login'}
                >
                  Go to Login
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
